import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Landing from "./Landing";
import JobOpenings from "./JobOpenings";
import Process from "./Process";
import {
  FaBullseye,
  FaChartLine,
  FaHandHoldingHeart,
  FaHandshake,
  FaStar,
  FaUsers,
  FaChevronDown,
  FaChevronUp
} from "react-icons/fa";

const Carrer = () => {
  const [openFAQ, setOpenFAQ] = useState(0); // First FAQ is open by default

  const faqData = [
    {
      question: "I\u2019ve submitted my application \u2014 what happens next?",
      answer: "Our Talent Acquisition team will review your profile, and we\u2019ll reach out if there\u2019s a good match to talk about the next steps."
    },
    {
      question: "How long does it take to hear back after applying?",
      answer: "We typically review applications within 1-2 weeks. Due to the volume of applications, we may not be able to respond to every applicant, but we appreciate your interest in joining Nirantara."
    },
    {
      question: "What does the recruitment process at Nirantara look like?",
      answer: "Our process usually starts with an initial screening, followed by 2\u20133 interviews, and may include a short assignment, assessment, or case discussion for some roles."
    },
    {
      question: "Are interviews conducted in person or virtually?",
      answer: "Interviews are usually conducted virtually, with in-person discussions arranged for certain roles when needed."
    },
    {
      question: "How will I be informed about the interview results?",
      answer: "We\u2019ll get back to you via email or phone once a decision is made. If selected, we\u2019ll walk you through the next steps, and if not, we\u2019ll share feedback where possible and encourage you to apply again in the future."
    },
    {
      question: "If I don\u2019t get selected, can I apply again in the future?",
      answer: "Yes, you\u2019re welcome to apply again after six months from your last interview or application date, whichever is later."
    },
    {
      question: "Does Nirantara offer internships?",
      answer: "Yes, we offer unpaid internships, depending on project availability at the time of application."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const whyJoinUsCards = [
    {
      icon: <FaBullseye />,
      title: "PURPOSE WITH IMPACT",
      description: "Be part of a sustainability-driven organisation where environmental responsibility, social progress, and good governance shape everything we do."
    },
    {
      icon: <FaChartLine />,
      title: "GROWTH THAT EMPOWERS",
      description: "Learn, lead, and keep growing through mentorship, continuous learning, and career advancement opportunities."
    },
    {
      icon: <FaHandHoldingHeart />,
      title: "WELL-BEING THAT MATTERS",
      description: "Stay covered with Group Health Insurance (GHI) and Group Personal Accident (GPA) policies, annual health check-ups, and generous paid leaves."
    },
    {
      icon: <FaHandshake />,
      title: "COLLABORATION AT THE CORE",
      description: "Work alongside passionate, diverse teams who believe great ideas come from working together."
    },
    {
      icon: <FaStar />,
      title: "DRIVEN BY EXCELLENCE",
      description: "We set high standards and celebrate progress together."
    },
    {
      icon: <FaUsers />,
      title: "CULTURE & CONNECTION",
      description: "Enjoy Friday lunches, an open-door policy, and a workplace built on trust and belonging."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Careers at Nirantara - ESG & Sustainability Job Opportunities</title>
        <meta name="description" content="Explore ESG and sustainability career opportunities at Nirantara. Join a purpose-driven team shaping environmental, social, and governance impact with growth, learning, and meaningful work." />
        <meta name="keywords" content="ESG jobs, sustainability careers, careers at Nirantara, ESG consulting jobs, environmental sustainability jobs, corporate ESG roles, ESG career opportunities" />
        <link rel="canonical" href="https://nirantara.solutions/career" />
      </Helmet>
      <Landing />
      <div className="flex flex-col items-center w-full gap-8 mb-8 md:mb-20 montserrat bg-white">
        {/* Join Our Journey Section */}
        <section className="w-full py-16 text-gray-800" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
          <div className="max-w-[1400px] mx-auto text-center">
            <h1 className="mb-6 text-4xl font-bold text-black md:text-6xl">Join Our Journey</h1>
            <p className="max-w-4xl mx-auto text-lg leading-relaxed text-black md:text-xl">
              At Nirantara, we're not just building a business - we're shaping a sustainable future. Our teams combine purpose, innovation, and integrity to create lasting impact across everything we do. If you're looking for a place where your ideas matter, your growth is supported, and your work creates real-world change - this is it.
            </p>
          </div>
        </section>

        {/* Current Openings Section */}
        <JobOpenings />

        {/* Stay Connected Section */}
        <section className="w-full py-16" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
          <div className="max-w-[1400px] mx-auto">
            <div className="bg-[#EB7E0E] rounded-2xl p-8 md:p-12 text-white">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">Stay Connected With Future Opportunities</h2>
              <p className="mb-6 text-lg leading-relaxed md:text-xl">
                If you don't see a role that fits right now, we'd still love to hear from you. Send your profile to hr@nirantara.solutions, and we'll reach out when a suitable opportunity becomes available.
              </p>
              <a
                href="mailto:hr@nirantara.solutions"
                className="inline-block px-6 py-3 text-lg font-semibold text-[#EB7E0E] bg-white border-2 border-white rounded-lg hover:bg-transparent hover:text-white transition-colors"
              >
                Send Your Profile
              </a>
            </div>
          </div>
        </section>

        {/* Why Join Us Section */}
        <section className="w-full py-16 text-gray-800" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
          <div className="max-w-[1400px] mx-auto">
            <h2 className="mb-12 text-4xl font-bold text-center text-black md:text-5xl">Why Join Us ?</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {whyJoinUsCards.map((card, index) => (
                <div
                  key={index}
                  className="flex flex-col p-6 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-center w-12 h-12 mb-4 bg-[#EB7E0E] rounded-lg text-white text-xl">
                    {card.icon}
                  </div>
                  <h3 className="mb-3 text-lg font-bold text-black">{card.title}</h3>
                  <p className="text-sm leading-relaxed text-gray-700">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recognised Section */}
        <section className="w-full py-16" style={{ overflow: 'visible' }}>
          <div className="relative w-full bg-[#1C486B] min-h-[400px] md:min-h-[350px]">
            {/* Thin white line at top */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-white z-10"></div>

            {/* Centered content container */}
            <div className="relative flex flex-col md:flex-row items-center justify-center min-h-[400px] md:min-h-[350px] w-full max-w-[1400px] mx-auto" style={{ paddingLeft: '8%', paddingRight: '8%', paddingBottom: '2rem' }}>
              {/* Text section */}
              <div className="flex-1 text-white" style={{ paddingTop: '2rem' }}>
                <h2 className="mb-4 text-3xl font-bold md:text-4xl leading-tight">
                  Recognised for How We Work,<br />
                  Not Just What We Do
                </h2>
                <p className="text-lg leading-relaxed md:text-xl">
                  We're proud to be a Great Place to Work Certified organisation, recognised for our open, collaborative, and empowering work environment.
                </p>
              </div>

              {/* Image section */}
              <div className="flex-shrink-0 md:ml-8" style={{ marginTop: 0, paddingTop: 0 }}>
                <img
                  src="/great-place-to-work.png"
                  alt="Great Place To Work Certified"
                  className="h-auto max-w-[180px] md:max-w-[220px] drop-shadow-lg"
                  style={{ display: 'block' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full py-16 text-gray-800 bg-white" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
          <div className="max-w-[1400px] mx-auto">
            <h2 className="mb-12 text-4xl font-bold text-center text-black md:text-5xl">Application FAQs</h2>
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</span>
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-[#EB7E0E] rounded-full text-white">
                      {openFAQ === index ? (
                        <FaChevronUp className="text-sm" />
                      ) : (
                        <FaChevronDown className="text-sm" />
                      )}
                    </div>
                  </button>
                  {openFAQ === index && (
                    <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Carrer;
